import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Deliverable } from '@finley-technologies/finley-types';

import {
  useGetUserQuery,
  useGetDeliverablesLazyQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DownloadCurrentDeliverableSubmissionIconButton } from './DeliverableSubmission/Download';
import { ResponsibleParties } from './ResponsibleParties';
import { getDeliverablesUrl } from './utils';
import { Alert } from 'packages/common-frontend/components/Alert';
import { FilterDatePicker } from 'packages/common-frontend/components/FilterDatePicker';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  PaginationControl,
  usePaginationLimit,
} from 'packages/common-frontend/components/PaginationControl';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import { StatusContainer } from 'packages/deliverables/ui/StatusContainer';
// import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import { FiChevronRight } from 'react-icons/fi';

type Props = {
  responsiblePartyId: string;
  activeCreditFacilities: { slug: string; id: string }[];
  organizationSelect?: React.ReactElement;
  optionalDescription?: React.ReactElement;
  queryParams?: {
    startDate?: number;
    endDate?: number;
    statuses?: Deliverable.v3DEPRECATED.ProgressStatus[];
    creditFacilityIds?: string[];
  };
};
export const Deliverables = ({
  responsiblePartyId,
  organizationSelect,
  activeCreditFacilities,
  optionalDescription,
  queryParams,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const statusOptions = useMemo(
    () =>
      _.reduce(
        Deliverable.v3DEPRECATED.ProgressStatuses,
        (
          acc: {
            label: string;
            value: Deliverable.v3DEPRECATED.ProgressStatus;
          }[],
          status,
        ) => {
          if (status !== 'resubmit') {
            acc.push({
              label: commonUtils.toSentenceCase(status),
              value: status,
            });
          }
          return acc;
        },
        [],
      ),
    [],
  );

  const [startDate, setStartDate] = useState<Date | null>(
    queryParams?.startDate ? new Date(queryParams.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    queryParams?.endDate ? new Date(queryParams.endDate) : null,
  );
  const [statuses, setStatuses] = useState<
    Deliverable.v3DEPRECATED.ProgressStatus[]
  >(queryParams?.statuses || _.map(statusOptions, 'value'));
  const [creditFacilityIds, setCreditFacilityIds] = useState<string[]>(
    queryParams?.creditFacilityIds || _.map(activeCreditFacilities, 'id'),
  );
  const [organizationId, setOrganizationId] = useState<string | null>(
    responsiblePartyId,
  );
  const { pageLimit, setPageLimit, options } = usePaginationLimit(10, [
    10,
    25,
    50,
  ]);

  useEffect(() => {
    setCreditFacilityIds(_.map(activeCreditFacilities, 'id'));
  }, [activeCreditFacilities]);

  useEffect(() => {
    setOrganizationId((oldOrganizationId) =>
      oldOrganizationId ? responsiblePartyId : null,
    );
  }, [responsiblePartyId]);

  const [
    getDeliverables,
    { data, loading },
  ] = useGetDeliverablesLazyQueryGQLType({
    fetchPolicy: 'no-cache',
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, navigate);
    },
  });

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const refetchReports = useCallback(
    (_offset: number) => {
      getDeliverables({
        variables: {
          responsiblePartyId,
          organizationId,
          creditFacilityIds,
          limit: pageLimit,
          startDate: startDate
            ? commonDate.transformToStartOfDay(startDate).getTime()
            : null,
          endDate: endDate
            ? commonDate.transformToEndOfDay(endDate).getTime()
            : null,
          statuses,
          offset: _offset,
        },
      });
    },
    [
      getDeliverables,
      responsiblePartyId,
      organizationId,
      creditFacilityIds,
      pageLimit,
      startDate,
      endDate,
      statuses,
    ],
  );

  useEffect(() => {
    refetchReports(0);
  }, [
    refetchReports,
    activeCreditFacilities,
    creditFacilityIds,
    pageLimit,
    endDate,
    startDate,
  ]);

  if (
    !data ||
    !data.deliverables ||
    loading ||
    !userData ||
    !userData.user ||
    userLoading
  ) {
    return <Skeleton data-testid="loading-deliverables-view" height="600px" />;
  }

  const {
    deliverables: {
      nodes: deliverables,
      offsetInfo: { hasPreviousPage, hasNextPage, offset },
    },
  } = data;

  const {
    organization: { id: requestingOrganizationId, name: organizationName },
  } = userData.user;

  const deliverablesUrl = getDeliverablesUrl(location.pathname);

  return (
    <Box height="100%">
      <Flex justify="space-between">
        <Heading as="h2" mb={optionalDescription ? '8px' : '16px'} pb="16px">
          Reporting Deliverables
        </Heading>
        <Box>{organizationSelect}</Box>
      </Flex>
      {optionalDescription && <Box mb="32px">{optionalDescription}</Box>}
      {/* SARAH TODO: add worklease generation stuff here (use for v4 just in case need to manually regenerate a worklease) */}
      <PaginationControl
        pageLimit={pageLimit}
        setPageLimit={setPageLimit}
        options={options}
        limitLabel="Deliverables per page"
        hasPreviousPage={hasPreviousPage}
        hasNextPage={hasNextPage}
        onPrevious={() => refetchReports(offset - pageLimit)}
        onNext={() => refetchReports(offset + pageLimit)}
        filters={
          <HStack spacing="10" mb="10px">
            <Box>
              <Select
                options={[
                  {
                    label: `View ${organizationName} Deliverables`,
                    value: null,
                  },
                  { label: 'View All Deliverables', value: responsiblePartyId },
                ]}
                value={organizationId}
                onChange={(value) => {
                  if (value) setOrganizationId(value);
                }}
              />
            </Box>
            <Box>
              <IsolatedMultiSelect
                options={_.map(activeCreditFacilities, ({ id, slug }) => ({
                  label: commonUtils.creditFacilitySlugToName(slug),
                  value: id,
                }))}
                initialSelections={creditFacilityIds}
                onApplyOptions={setCreditFacilityIds}
                optionsHeader="Credit Facilities"
                titleText={
                  creditFacilityIds.length > 0
                    ? `${creditFacilityIds.length} selected credit facilit${
                        creditFacilityIds.length === 1 ? 'y' : 'ies'
                      }`
                    : 'No credit facilities selected'
                }
                applyButtonText="Apply filters"
              />
            </Box>
            <Box>
              <IsolatedMultiSelect
                options={statusOptions}
                initialSelections={statuses}
                onApplyOptions={setStatuses}
                optionsHeader="Deliverable Statuses"
                titleText={
                  statuses.length > 0
                    ? `${statuses.length} selected deliverable status${
                        statuses.length === 1 ? '' : 'es'
                      }`
                    : 'No deliverable statuses selected'
                }
                applyButtonText="Apply filters"
              />
            </Box>
            <VStack>
              <FilterDatePicker
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                label="Due Date"
              />
            </VStack>
          </HStack>
        }
      >
        {_.isEmpty(deliverables) ? (
          <Alert status="info" mt="35px" variant="subtle">
            <Text>There are currently no deliverables to view</Text>
          </Alert>
        ) : (
          <Table borderRadius="lg">
            <Thead>
              <Tr>
                <Th textAlign="center">Responsible Parties</Th>
                <Th>Credit Facility</Th>
                <Th>Deliverable</Th>
                <Th>Section</Th>
                <Th>Due Date</Th>
                <Th>Status</Th>
                <Th>Submission Date</Th>
                <Th />
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {_.map(deliverables, (deliverable) => {
                const {
                  id,
                  name,
                  section,
                  dueDate,
                  // status,
                  description,
                  creditFacilityName,
                  responsibleParties,
                  isDeliverableResponsibleParty,
                  isDeliverableDownloadPermissionedParty,
                  isDeliverableViewPermissionedParty,
                  // updated_at,
                } = deliverable;
                const canView = Boolean(
                  isDeliverableResponsibleParty ||
                    isDeliverableViewPermissionedParty,
                );
                const canDownload = Boolean(
                  isDeliverableResponsibleParty ||
                    isDeliverableDownloadPermissionedParty,
                );

                return (
                  <Tr key={`${id}`}>
                    <Td textAlign="center">
                      <ResponsibleParties
                        responsibleParties={responsibleParties}
                        requestingOrganizationId={requestingOrganizationId}
                      />
                    </Td>
                    <Td>{creditFacilityName}</Td>
                    <Td>{name}</Td>
                    <Td>
                      <HStack>
                        <Text>{section}</Text>
                        <Tooltip bg="gray.500" hasArrow label={description}>
                          <InfoOutlineIcon />
                        </Tooltip>
                      </HStack>
                    </Td>
                    <Td>{commonDate.fullDateStrToHumanDateString(dueDate)}</Td>
                    <Td textAlign="center">
                      <StatusContainer
                        canUpdate={false}
                        deliverable={deliverable}
                      />
                    </Td>
                    <Td>
                      {/* @jendevelops TODO: add DeliverableValueSubmission render */}
                      {canDownload && (
                        <>
                          {deliverable.currentSubmission?.__typename ===
                          'DeliverableFileSubmission'
                            ? commonDate.epochToLocaleDateString(
                                deliverable.currentSubmission.created_at,
                                undefined,
                                DateTime.DATE_FULL,
                              )
                            : null}
                        </>
                      )}
                    </Td>
                    <Td textAlign="center">
                      {/* @jendevelops TODO: add DeliverableValueSubmission render */}
                      {canDownload && (
                        <>
                          {deliverable.currentSubmission?.__typename ===
                          'DeliverableFileSubmission' ? (
                            <DownloadCurrentDeliverableSubmissionIconButton
                              deliverableId={deliverable.id}
                              fileName={deliverable.currentSubmission.fileName}
                            />
                          ) : null}
                        </>
                      )}
                    </Td>
                    <Td textAlign="right">
                      {canView && (
                        <Tooltip bg="gray.500" hasArrow label="More details">
                          <IconButton
                            aria-label="more details"
                            icon={<FiChevronRight />}
                            variant="ghost"
                            onClick={() =>
                              navigate(
                                deliverablesUrl
                                  ? `${deliverablesUrl}/${id}`
                                  : `/deliverables/${id}`,
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </PaginationControl>
    </Box>
  );
};
